import React from "react";
import { useSelector, useDispatch } from "react-redux";
import Moment from "moment";
import _ from "lodash";
import { makeStyles, Grid, Card, CardActions, CardContent } from "@material-ui/core";
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from "@material-ui/lab";
import { Button, Typography, Page } from "@packages/ui";
import * as appActions from "../../actions/appActions";
import * as constants from "../../utils/constants";
import { useCurrentRental } from "src/hooks/rentalHooks";

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: 15,
    paddingBottom: theme.spacing(3),
  },
  activeCard: {
    borderBottom: `5px solid ${theme.palette.primary.main}`,
  },
  mainCard: {
    borderBottom: `5px solid ${theme.palette.primary.main}`,
  },
}));

const RentalHistory = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const app = useSelector((state) => ({ ...state.app }));
  const rentals = useSelector((state) => [...state.app.rentals]);
  const currentRental = useCurrentRental();

  const selectRental = (e, ra) => {
    dispatch({ type: "SAVESTATE", newstate: { currentRental: ra } });
    dispatch(appActions.openPopper());
  };

  return (
    <Page className={classes.root} title="Rental Summary - My Journey Portal">
      <Grid container direction="row">
        <Grid item xs={12}>
          <Typography variant={"h1"} style={{ marginBottom: 30 }}>
            Rental Summary
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography>
            <strong>Welcome to My Journey Portal</strong>
            <br />
            <br />
          </Typography>
        </Grid>
        <Grid item xs={12}>
          {currentRental?.brand !== "Hertz" ? (
            <div>
              <Typography>
                Below is a summary of your recent rental with {currentRental?.brand}.
                <br />
                <br />
                To view more information relating to this rental, please use the menu to display
                additional documentation such as invoices and pre-rental photos.
              </Typography>
            </div>
          ) : currentRental?.gold_number ? (
            <div>
              <Typography>
                Below is a summary of your recent rental history with {currentRental.brand}.
              </Typography>
              <br />
              <Typography>
                To view more information relating to your recent {currentRental.brand} rental,
                please make your selection from the list below and then use the menu to display
                additional documentation such as invoices and pre-rental photos.
              </Typography>
            </div>
          ) : (
            <Typography>
              Hertz Gold Plus Rewards members can view additional rental history. Click{" "}
              <a
                href={constants.HERTZ_JOIN_GOLD_MEMBER}
                rel="noreferrer"
                target="_blank"
                style={{ color: "#428bff" }}
              >
                here
              </a>{" "}
              to join Gold Plus Rewards for free and enjoy a faster, more rewarding rental
              experience.
            </Typography>
          )}
        </Grid>
        <Grid item xs={12}>
          <Typography style={{ marginBottom: 30 }}>
            <br />
            <strong>Thank you for choosing {currentRental?.brand}. </strong>
          </Typography>
        </Grid>

        <Grid item xs={12}>
          {rentals?.length > 1 ? (
            <Timeline>
              <TimelineItem>
                <TimelineSeparator>
                  <TimelineDot color={"primary"} />
                </TimelineSeparator>
                <TimelineContent style={{ paddingBottom: 0, maxWidth: 500 }}>
                  <Card variant="outlined" className={classes.mainCard}>
                    <CardContent>
                      <Grid container direction="row" justify="flex-start" alignItems="flex-start">
                        <Typography style={{ fontSize: 10, marginTop: -10, marginBottom: 10 }}>
                          Currently Selected
                        </Typography>
                      </Grid>
                      <Grid container direction="row" justify="center" alignItems="center">
                        <Grid item xs={12} style={{ textAlign: "center" }}>
                          <Typography variant={"h4"} color="textSecondary">
                            <strong>{currentRental.rental_agreement_number}</strong>
                          </Typography>
                        </Grid>
                        <Grid item xs={12} style={{ textAlign: "center" }}>
                          <Typography variant={"subtitle1"} style={{ fontSize: 14 }}>
                            {Moment(
                              _.get(currentRental, `pickup.dateTime`, ""),
                              "YYYY-MM-DD hh:mm"
                            ).format("DD/MM/YYYY")}{" "}
                            -{" "}
                            {Moment(
                              _.get(currentRental, `dropoff.dateTime`, ""),
                              "YYYY-MM-DD hh:mm"
                            ).format("DD/MM/YYYY")}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid
                        container
                        direction="row"
                        justify="flex-start"
                        alignItems="flex-start"
                      ></Grid>
                    </CardContent>
                  </Card>
                </TimelineContent>
              </TimelineItem>
            </Timeline>
          ) : (
            ""
          )}
        </Grid>
        <Grid item xs={12}>
          <Timeline>
            {Object.keys(rentals).map((key) => (
              <TimelineItem key={key}>
                <TimelineSeparator>
                  <TimelineDot
                    color={
                      rentals[key].rental_agreement_number === app.currentRental
                        ? "primary"
                        : "inherit"
                    }
                  />
                  <TimelineConnector />
                </TimelineSeparator>
                <TimelineContent style={{ paddingBottom: 0, maxWidth: 500 }}>
                  <Card
                    variant="outlined"
                    className={
                      rentals[key].rental_agreement_number === app.currentRental
                        ? classes.activeCard
                        : ""
                    }
                  >
                    <CardContent>
                      <Grid container direction="row" justify="center" alignItems="center">
                        <Grid item xs={12} style={{ textAlign: "center" }}>
                          <Typography variant={"h4"} color="textSecondary">
                            <strong>{rentals[key].rental_agreement_number}</strong>
                          </Typography>
                        </Grid>
                        <Grid item xs={12} style={{ textAlign: "center" }}>
                          <Typography variant={"subtitle1"} style={{ fontSize: 14 }}>
                            {Moment(
                              _.get(rentals, `${key}.pickup.dateTime`, ""),
                              "YYYY-MM-DD hh:mm"
                            ).format("DD/MM/YYYY")}{" "}
                            -{" "}
                            {Moment(
                              _.get(rentals, `${key}.dropoff.dateTime`, ""),
                              "YYYY-MM-DD hh:mm"
                            ).format("DD/MM/YYYY")}{" "}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid
                        container
                        direction="row"
                        justify="flex-start"
                        alignItems="flex-start"
                      ></Grid>
                    </CardContent>
                    <CardActions style={{ display: "inherit", textAlign: "center" }}>
                      <Button
                        size="small"
                        type="button"
                        color="yellow"
                        title={
                          rentals[key].rental_agreement_number === app.currentRental
                            ? "Selected"
                            : "Select"
                        }
                        disabled={rentals[key].rental_agreement_number === app.currentRental}
                        onClick={(e) => selectRental(e, rentals[key].rental_agreement_number)}
                      />
                    </CardActions>
                  </Card>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        </Grid>
      </Grid>
    </Page>
  );
};

export default RentalHistory;
